import { Box3, Matrix4, Plane, Vector3 } from 'three';

import { SettingModelSettingType } from 'api/models';
import {
    clearHoveredHighlight,
    cloneArticleInstances,
    getArticleId,
    getArticleInstanceData,
    getDistance3D,
    HoverArticleGroupEntry,
    hoveredGroupByArticle,
    hoveredGroupInstanceIds,
    resolveHoverArticleRef
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import {
    alignSelectionToCursorPoint,
    anchorPosition,
    appendGroupedInstance,
    applyMoveToInstance,
    applySnapOffsetToInstances,
    computePerInstanceWallDelta,
    dragAnchorPositionRef,
    dragColliderCache,
    dragDelta,
    draggingBoxStatus,
    getAnchorAlignment,
    getAnchorDragContext,
    getAnchorPosition,
    groupSelectedInstancesByArticle,
    handleDragSettings,
    handleMarkerVisibility,
    hasAnyRegisteredDragRefs,
    instanceToArticleIdMap,
    instanceToPoiIndexMap,
    isMixedAlignmentSelection,
    isTopBottomCameraView,
    perInstanceDragOffsets,
    perInstancePrevIntersections,
    perInstancePlanes,
    projectDeltaToWorkingPlane,
    raycastPlane,
    scheduleDraggingBoxComputation,
    targetPosition,
    translateDraggingBox,
    translateInstanceParts,
    updateInstanceToArticleIdMap,
    updateInstanceToPoiIndexMap,
    initializePerInstancePlanesAndIntersections,
    placeInstanceOnWall,
    initialPoint,
    getConnectorArticleId,
    getConnectorSnapPlane,
    getConnectorSnapSource,
    findNearestConnectorSnapCandidate,
    applyRotationToSelectedInstances,
    adjustedSnapSource,
    getPreferredInsertionPlane,
    shouldUseZAxisOnlyDrag,
    activePoiGuids,
    retargetNormal,
    retargetProjectedPoint,
    updateCornerPlacementTarget,
    updateWallPlacementTarget
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/dragUtils';
import { POIFilterType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiFilterType';
import { yieldToMain } from 'app/modules/wallforming/components/state-management/placement/utils/schedulerUtils';
import {
    DRAG_START_THRESHOLD,
    DRAGGING_BOX_THRESHOLD,
    MATRIX_UPDATE_CHUNK_SIZE,
    SNAP_THROTTLE_MS
} from 'shared/constants';

import type { ArticleDragReference } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleDragReference';
import type { ArticleInstance } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleInstance';
import type { ArticleReference } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleReference';
import type { WallEdge } from 'app/modules/wallforming/components/scenes/objects/helpers/types/wallEdge';
import type { ArticleEventHandlerSliceState } from 'app/modules/wallforming/components/state-management/placement/states/articleEventHandlerSliceState';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';
import type { RefObject } from 'react';
import type { StateCreator } from 'zustand';

const pointerDownScreen = { x: 0, y: 0 };
let isDragEnabled = false;

// Alignment state for the current drag session.
let isMixedAlignmentDrag = false;

let isGroupDragActive = false;
const groupMemberIds = new Set<string>();

const sharedAlignmentNormal = new Vector3();
let isTopBottomViewDrag = false;

const snapOffset = new Vector3();
const planeIntersectPoint = new Vector3();

// Reusable vectors for per-instance wall projection during mixed-alignment drag.
const perInstanceDelta = new Vector3();
// Reusable vector for per-frame wall-plane ray intersection (avoids per-frame allocations).
const perInstanceNewIntersect = new Vector3();
// Reusable vector for projecting snap offset onto per-instance wall planes.
const snapProjDelta = new Vector3();
// Captures the anchor's actual wall-plane movement delta for dragging-box sync.
const anchorMovedDelta = new Vector3();

const instancesByArticleId = new Map<string, string[]>();

let snapAnchorInstanceId: string | undefined;

const dragStartPoint = new Vector3();
let hasDragStartPoint = false;

let lastSnapTime = 0;
let cachedSnapPoints: Vector3[] | null = null;
let isCornerSnapActive = false;

let hasCachedSnapAnchorPosition = false;
let hasDragStartReferenceOffset = false;

const worldCorner = new Vector3();
const projectedCorner = new Vector3();
const cachedIntersect = new Vector3();

const cachedSnapAnchorPosition = new Vector3();
const currentSnapAnchorPosition = new Vector3();

const dragStartReferenceOffset = new Vector3();
const lastCursorPlanePoint = new Vector3();

let hasLastCursorPlanePoint = false;

let isDragStart = true;
let hasPreviousDragPoint = false;

let lastSnappedEdge: WallEdge | null = null;
let connectorSnapCandidateIndex: number | null = null;

export function syncDragStateOnPoiChange(selectedInstanceIds: string[], targetPlane: Plane): void {
    targetPosition.copy(retargetProjectedPoint);
    initialPoint.copy(retargetProjectedPoint);
    planeIntersectPoint.copy(retargetProjectedPoint);
    lastCursorPlanePoint.copy(retargetProjectedPoint);
    hasLastCursorPlanePoint = true;
    hasPreviousDragPoint = true;

    sharedAlignmentNormal.copy(retargetNormal);
    isMixedAlignmentDrag = false;
    isTopBottomViewDrag = isTopBottomCameraView(raycastPlane.normal);

    perInstancePlanes.clear();
    perInstancePrevIntersections.clear();
    for (const instanceId of selectedInstanceIds) {
        perInstancePlanes.set(instanceId, targetPlane.clone());
        perInstancePrevIntersections.set(instanceId, retargetProjectedPoint.clone());
    }
}

function resetDragSettings(): void {
    snapAnchorInstanceId = undefined;
    draggingBoxStatus.isPending = false;
    draggingBoxStatus.isComputed = false;

    cachedSnapPoints = null;
    isCornerSnapActive = false;
    hasCachedSnapAnchorPosition = false;
    hasDragStartReferenceOffset = false;
    hasDragStartPoint = false;
    lastSnapTime = 0;
    hasPreviousDragPoint = false;
    isDragEnabled = false;
    isMixedAlignmentDrag = false;
    sharedAlignmentNormal.set(0, 0, 0);
    isTopBottomViewDrag = false;
    instanceToPoiIndexMap.clear();
    dragColliderCache.clear();
    perInstanceDragOffsets.clear();
    perInstancePlanes.clear();
    perInstancePrevIntersections.clear();
    dragAnchorPositionRef.current = null;
    activePoiGuids.clear();
    lastSnappedEdge = null;
    connectorSnapCandidateIndex = null;
}

export const createArticleEventHandlerSlice: StateCreator<PlacementState, [], [], ArticleEventHandlerSliceState> = (
    set,
    get
) => ({
    recomputeTransformationBox: false,
    toggleRecomputeTransformationBox: () =>
        set((state) => ({ recomputeTransformationBox: !state.recomputeTransformationBox })),

    hoveredArticleInstanceIds: [],
    setHoveredArticleInstanceIds: (instanceIds) => set({ hoveredArticleInstanceIds: instanceIds }),

    isArticleDirectlySelectedRef: { current: false },
    setIsArticleDirectlySelectedRef: (isSelected) => {
        const state = get();
        if (state.isArticleDirectlySelectedRef.current !== isSelected) {
            state.isArticleDirectlySelectedRef.current = isSelected;
        }
    },

    draggingBoxRef: {
        current: {
            isActive: false,
            boundingBox: new Box3(),
            localToWorld: new Matrix4()
        }
    },

    articleDragRefsMap: { current: new Map<string, ArticleDragReference>() },
    getSelectedArticleDragReference: (articleInstanceId) => {
        const state = get();
        const instanceId = articleInstanceId ?? Object.keys(state.articleInstanceSelections)[0];
        if (!instanceId) {
            return undefined;
        }
        let articleId = instanceToArticleIdMap.get(instanceId);
        if (!articleId) {
            // lazily resolve from state.articles (e.g. after box selection)
            articleId = Object.keys(state.articles).find((id) => instanceId in state.articles[id].articleInstances);
            if (articleId) {
                instanceToArticleIdMap.set(instanceId, articleId);
            }
        }
        if (!articleId) {
            return undefined;
        }
        return state.articleDragRefsMap.current.get(articleId);
    },

    registerArticleDragReference: (articleId, articleRef) => {
        const state = get();
        state.articleDragRefsMap.current.set(articleId, { articleRef });

        // Fallback: update z position when dragging got interrupted during top/bottom view insertion
        const isDragRefRegistered = state.articleDragRefsMap.current.get(articleId ?? '') !== undefined;
        if (state.isZPositionUpdatePending && isDragRefRegistered) {
            const selectedInstanceIds = Object.keys(state.articleInstanceSelections);
            if (selectedInstanceIds.length === 1) {
                placeInstanceOnWall(selectedInstanceIds[0], articleId, state, state.articleDragRefsMap.current);
                state.setIsZPositionUpdatePending(false);
            }
        }
    },
    unregisterArticleDragReference: (articleId) => {
        get().articleDragRefsMap.current.delete(articleId);
    },

    handleOnInsertArticleStart: (isDoubleClick) => {
        const state = get();
        if (state.arePivotControlsActive) {
            state.updatePivotControls();
        }

        state.setArticleInstanceSelections({});

        if (isDoubleClick) {
            state.resetHiddenPOIFilterTypes();
            state.setIsAutoDragActiveRef(true);
        }

        // Reset cursor plane point from a previous drag session
        hasLastCursorPlanePoint = false;
        state.setIsDragNewArticleStartRef(true);
    },

    updateAutoDragTarget: async (filterType, index) => {
        const state = get();
        const currentFilterType = state.poiSelection.current.filterType;
        if (!state.isAutoDragActiveRef.current) {
            return true;
        }

        if (filterType === undefined || index === undefined) {
            return false;
        }

        if (currentFilterType !== undefined && filterType !== currentFilterType) {
            return false;
        }

        const selectedInstanceIds = Object.keys(state.articleInstanceSelections);

        switch (filterType) {
            case POIFilterType.Wall:
                return updateWallPlacementTarget(state, selectedInstanceIds, index, snapAnchorInstanceId);
            case POIFilterType.Corner:
                return updateCornerPlacementTarget(state, get, selectedInstanceIds, index);
            default:
                return false;
        }
    },

    syncSelectedInstancesToCursor: () => {
        const state = get();
        if (!state.isAutoDragActiveRef.current) {
            return false;
        }

        const activeManualConnectorArticleId = getConnectorArticleId(state, snapAnchorInstanceId);
        const manualInsertionPlane = getConnectorSnapPlane(state, activeManualConnectorArticleId);
        const raycaster = state.mainRaycasterRef.current;
        if (!raycaster) {
            return false;
        }

        // Mixed-alignment drag: update each instance via its own plane ray
        if (isMixedAlignmentDrag && perInstancePlanes.size > 0) {
            const { ray } = raycaster;
            const dragRefsMap = state.articleDragRefsMap.current;
            const selectedInstanceIds = Object.keys(state.articleInstanceSelections);
            let moved = false;

            for (const instanceId of selectedInstanceIds) {
                const wallPlane = perInstancePlanes.get(instanceId);
                if (!wallPlane || !ray.intersectPlane(wallPlane, perInstanceNewIntersect)) {
                    continue;
                }
                const prevPoint = perInstancePrevIntersections.get(instanceId);
                if (!prevPoint) {
                    perInstancePrevIntersections.set(instanceId, perInstanceNewIntersect.clone());
                    continue;
                }
                perInstanceDelta.subVectors(perInstanceNewIntersect, prevPoint);
                if (perInstanceDelta.x === 0 && perInstanceDelta.y === 0 && perInstanceDelta.z === 0) {
                    continue;
                }
                const articleId = instanceToArticleIdMap.get(instanceId);
                const dragRef = articleId ? dragRefsMap.get(articleId) : undefined;
                const instanceData = dragRef?.articleRef.current?.articleInstances[instanceId];
                if (!instanceData) {
                    continue;
                }
                translateInstanceParts(instanceData, perInstanceDelta);
                const cached = dragColliderCache.get(instanceId);
                if (cached) {
                    for (const p of cached) {
                        p.add(perInstanceDelta);
                    }
                }
                prevPoint.copy(perInstanceNewIntersect);
                moved = true;
            }

            return moved;
        }

        // For new-article insertion use the POI wall normal as constraint;
        // for regular drag use the shared instance alignment (or null for mixed).
        const preferredInsertionPlane = getPreferredInsertionPlane(state, manualInsertionPlane);

        const constraintNormal =
            state.isDragNewArticleActiveRef.current && manualInsertionPlane
                ? manualInsertionPlane.normal
                : state.isDragNewArticleActiveRef.current && state.workingPlane
                  ? state.workingPlane.normal
                  : !isMixedAlignmentDrag && sharedAlignmentNormal.lengthSq() > 0
                    ? sharedAlignmentNormal
                    : null;

        if (
            state.isDragNewArticleActiveRef.current &&
            preferredInsertionPlane &&
            raycaster.ray.intersectPlane(preferredInsertionPlane, planeIntersectPoint)
        ) {
            return alignSelectionToCursorPoint(state, planeIntersectPoint, snapAnchorInstanceId, constraintNormal);
        }

        if (raycaster.ray.intersectPlane(raycastPlane, planeIntersectPoint)) {
            return alignSelectionToCursorPoint(state, planeIntersectPoint, snapAnchorInstanceId, constraintNormal);
        }

        return false;
    },

    handleOnDragArticle: (event) => {
        let state = get();
        if (state.isTransformationActiveRef.current) {
            return;
        }

        // Skip dragging if manual placement data is loading
        if (get().waitForPlacementData) {
            return;
        }

        const pointerConnectorArticleId = getConnectorArticleId(state, snapAnchorInstanceId);
        const pointerManualInsertionPlane = getConnectorSnapPlane(state, pointerConnectorArticleId);
        const preferredPointerPlane =
            (state.isDragNewArticleStartRef.current || state.isDragNewArticleActiveRef.current) &&
            getPreferredInsertionPlane(state, pointerManualInsertionPlane);

        if (preferredPointerPlane && event.ray.intersectPlane(preferredPointerPlane, planeIntersectPoint)) {
            lastCursorPlanePoint.copy(planeIntersectPoint);
            hasLastCursorPlanePoint = true;
        } else if (event.ray.intersectPlane(raycastPlane, planeIntersectPoint)) {
            lastCursorPlanePoint.copy(planeIntersectPoint);
            hasLastCursorPlanePoint = true;
        }

        if ((event.buttons & 1) === 0 && !get().isAutoDragActiveRef.current) {
            return;
        }

        // update and check drag start threshold
        if (!isDragEnabled) {
            const dx = event.clientX - pointerDownScreen.x;
            const dy = event.clientY - pointerDownScreen.y;
            if (Math.sqrt(dx * dx + dy * dy) > DRAG_START_THRESHOLD) {
                isDragEnabled = true;
            } else {
                return;
            }
        }

        if (get().isArticleDirectlySelectedRef.current && !get().isDragActiveRef.current) {
            get().setIsDragActiveRef(true);
        }

        if (get().isDragNewArticleStartRef.current) {
            state.setIsDragNewArticleStartRef(false);

            const selectedArticleId = state.selectedArticleId;
            const isConnectorInsertionRequested =
                selectedArticleId !== null &&
                state.connectorSnapData.articleId === selectedArticleId &&
                state.connectorSnapData.settingType === SettingModelSettingType.Connectors;

            if (
                isConnectorInsertionRequested &&
                (state.connectorSnapData.isLoading ||
                    state.connectorSnapData.candidates.length === 0 ||
                    state.connectorSnapData.plane === null)
            ) {
                return;
            }

            if (isConnectorInsertionRequested && state.connectorSnapData.isLoading) {
                return;
            }

            const insertionManualPlane = getConnectorSnapPlane(state, state.selectedArticleId);
            const insertionPlane = getPreferredInsertionPlane(state, insertionManualPlane);
            let insertionPosition: Vector3 | undefined;
            if (insertionPlane && event.ray.intersectPlane(insertionPlane, planeIntersectPoint)) {
                insertionPosition = planeIntersectPoint.clone();
            } else if (hasLastCursorPlanePoint) {
                insertionPosition = lastCursorPlanePoint;
            }

            get().insertArticleInstance(insertionPosition);
            state = get();
            state.setIsDragNewArticleActiveRef(true);
            state.setIsDragActiveRef(true);
            isDragStart = true;
        }

        if (!get().isDragActiveRef.current && !get().isDragNewArticleActiveRef.current) {
            return;
        }

        const selectedInstanceIds = Object.keys(state.articleInstanceSelections);
        const dragRefsMap = state.articleDragRefsMap.current;
        const workingPlane = state.workingPlane;

        // create instanceToArticleIdMap
        updateInstanceToArticleIdMap(selectedInstanceIds, state);
        const activeManualConnectorArticleId = getConnectorArticleId(
            state,
            snapAnchorInstanceId ?? selectedInstanceIds[0]
        );
        const manualInsertionPlane = getConnectorSnapPlane(state, activeManualConnectorArticleId);

        // check whether any selected instance has registered drag refs
        const hasAnyRegisteredRefs = hasAnyRegisteredDragRefs(selectedInstanceIds, dragRefsMap);
        if (!hasAnyRegisteredRefs) {
            if (event.ray.intersectPlane(raycastPlane, planeIntersectPoint)) {
                const normal =
                    state.isDragNewArticleActiveRef.current && manualInsertionPlane?.normal
                        ? manualInsertionPlane.normal
                        : state.isDragNewArticleActiveRef.current && workingPlane?.normal
                          ? workingPlane.normal
                          : !isMixedAlignmentDrag && sharedAlignmentNormal.lengthSq() > 0
                            ? sharedAlignmentNormal
                            : null;
                alignSelectionToCursorPoint(state, planeIntersectPoint, snapAnchorInstanceId, normal);
            }

            return;
        }

        const mainCameraControlsRef = state.mainCameraControlsRef;

        const dragAnchorId = snapAnchorInstanceId ?? selectedInstanceIds[0];
        const draggedArticleId = getConnectorArticleId(state, dragAnchorId);
        const isConnectorDrag =
            draggedArticleId !== null &&
            state.connectorSnapData.articleId === draggedArticleId &&
            state.connectorSnapData.settingType === SettingModelSettingType.Connectors &&
            (state.isDragNewArticleActiveRef.current || state.isDragActiveRef.current);

        // calculate drag offsets of the selected article instances
        if (mainCameraControlsRef.current && isDragStart) {
            const isDragNewArticle = state.isDragNewArticleActiveRef.current;
            isTopBottomViewDrag = isTopBottomCameraView(raycastPlane.normal);
            isMixedAlignmentDrag = isMixedAlignmentSelection(selectedInstanceIds, dragRefsMap);
            state.setIsZAxisDragActiveRef(shouldUseZAxisOnlyDrag(selectedInstanceIds, state));
            const preferredInsertionPlane = isDragNewArticle
                ? getPreferredInsertionPlane(state, manualInsertionPlane)
                : null;

            // Update groupMemberIds once at drag start
            groupMemberIds.clear();
            if (!isDragNewArticle) {
                for (const id of selectedInstanceIds) {
                    if (state.instanceToGroupMap[id]) {
                        groupMemberIds.add(id);
                    }
                }
            }
            isGroupDragActive = groupMemberIds.size > 0;

            if (!isMixedAlignmentDrag) {
                // Store the shared wall normal for delta projection during drag.
                const { anchorInstanceData: firstInstance } = getAnchorDragContext(selectedInstanceIds[0], dragRefsMap);
                if (firstInstance) {
                    getAnchorAlignment(firstInstance, sharedAlignmentNormal);
                }
            }

            updateInstanceToPoiIndexMap(selectedInstanceIds, state);

            // Update the active POI lookups
            activePoiGuids.clear();
            for (const instanceId of selectedInstanceIds) {
                const poiIndex = instanceToPoiIndexMap.get(instanceId);
                if (poiIndex !== undefined) {
                    const poiGuid = state.pois[poiIndex]?.poi?.poiGuid;
                    if (poiGuid) {
                        activePoiGuids.add(poiGuid);
                    }
                }
            }

            if (
                isTopBottomViewDrag &&
                isDragNewArticle &&
                state.selectedArticleId &&
                selectedInstanceIds.length === 1
            ) {
                placeInstanceOnWall(selectedInstanceIds[0], state.selectedArticleId, state, dragRefsMap);
            }

            const hasInitialPointIntersection = preferredInsertionPlane
                ? event.ray.intersectPlane(preferredInsertionPlane, initialPoint)
                : event.ray.intersectPlane(raycastPlane, initialPoint);

            if (!hasInitialPointIntersection) {
                return;
            }

            const usedragStartPoint = hasDragStartPoint && !isDragNewArticle && !state.isAutoDragActiveRef.current;
            const dragPoint = usedragStartPoint ? dragStartPoint : initialPoint;
            hasDragStartPoint = false;

            targetPosition.copy(dragPoint);
            hasPreviousDragPoint = true;

            if (!snapAnchorInstanceId || !selectedInstanceIds.includes(snapAnchorInstanceId)) {
                snapAnchorInstanceId = selectedInstanceIds[0];
            }

            // Keep a stable drag reference (grab point / insertionpoint) relative to the snap anchor.
            hasDragStartReferenceOffset = false;
            if (snapAnchorInstanceId) {
                const { anchorArticleId, anchorInstanceData } = getAnchorDragContext(snapAnchorInstanceId, dragRefsMap);
                if (anchorInstanceData && getAnchorPosition(anchorInstanceData, anchorPosition)) {
                    if (!dragAnchorPositionRef.current) {
                        dragAnchorPositionRef.current = anchorPosition.clone();
                    } else {
                        dragAnchorPositionRef.current.copy(anchorPosition);
                    }

                    raycastPlane.setFromNormalAndCoplanarPoint(raycastPlane.normal, anchorPosition);

                    if (isDragNewArticle && anchorArticleId) {
                        const insertionPoint = state.computeArticleInsertionPoint(anchorArticleId, anchorInstanceData);
                        if (insertionPoint) {
                            dragStartReferenceOffset.copy(dragPoint).sub(insertionPoint);
                            hasDragStartReferenceOffset = true;
                        }
                    }

                    if (!hasDragStartReferenceOffset) {
                        dragStartReferenceOffset.copy(dragPoint).sub(anchorPosition);
                        hasDragStartReferenceOffset = true;
                    }
                }
            }

            // Only use per-instance drag offsets for manual article dragging - otherwise move together as a rigid group
            if (isDragNewArticle) {
                perInstanceDragOffsets.clear();
                const instancePos = new Vector3();
                for (const instanceId of selectedInstanceIds) {
                    if (instanceId === snapAnchorInstanceId) {
                        continue;
                    }
                    const articleId = instanceToArticleIdMap.get(instanceId);
                    const dragRef = articleId ? dragRefsMap.get(articleId) : undefined;
                    const instanceData = dragRef?.articleRef.current?.articleInstances[instanceId];
                    if (instanceData && getAnchorPosition(instanceData, instancePos)) {
                        perInstanceDragOffsets.set(instanceId, dragPoint.clone().sub(instancePos));
                    }
                }
            }

            perInstancePlanes.clear();
            perInstancePrevIntersections.clear();
            initializePerInstancePlanesAndIntersections(
                selectedInstanceIds,
                dragRefsMap,
                event.ray,
                perInstancePlanes,
                perInstancePrevIntersections
            );

            if (isDragNewArticle || state.isAutoDragActiveRef.current) {
                const normal =
                    isDragNewArticle && manualInsertionPlane?.normal
                        ? manualInsertionPlane.normal
                        : isDragNewArticle && workingPlane?.normal
                          ? workingPlane.normal
                          : !isMixedAlignmentDrag && sharedAlignmentNormal.lengthSq() > 0
                            ? sharedAlignmentNormal
                            : null;

                const didAlignToCursor = alignSelectionToCursorPoint(state, dragPoint, snapAnchorInstanceId, normal);
                if (didAlignToCursor) {
                    targetPosition.copy(dragPoint);
                }
            }

            lastSnapTime = performance.now();

            const activeDraggingBox = selectedInstanceIds.length > DRAGGING_BOX_THRESHOLD;

            // hide all non-anchor instances at drag start
            if (activeDraggingBox) {
                instancesByArticleId.clear();
                const grouped = groupSelectedInstancesByArticle(
                    selectedInstanceIds.filter((instanceId) => instanceId !== snapAnchorInstanceId)
                );
                for (const [articleId, ids] of grouped) {
                    instancesByArticleId.set(articleId, ids);
                }
                for (const [articleId, ids] of instancesByArticleId) {
                    const dragRef = dragRefsMap.get(articleId);
                    if (dragRef) {
                        state.updateInstanceMatrices(true, dragRef.articleRef, false, ids, false);
                    }
                }
            }

            // remove colliders of the selected instances except the snap anchor
            const collidersToRemove =
                activeDraggingBox && snapAnchorInstanceId
                    ? selectedInstanceIds.filter((id) => id !== snapAnchorInstanceId)
                    : selectedInstanceIds;

            state.removeCollidersByRelations(collidersToRemove);
            handleDragSettings(isDragStart, state.mainWebGLRendererRef);
            isDragStart = false;

            if (
                (preferredInsertionPlane && event.ray.intersectPlane(preferredInsertionPlane, planeIntersectPoint)) ||
                event.ray.intersectPlane(raycastPlane, planeIntersectPoint)
            ) {
                targetPosition.copy(planeIntersectPoint);
            }
        }

        // handle article instance dragging
        if (mainCameraControlsRef.current && !isDragStart) {
            const preferredDragPlane = state.isDragNewArticleActiveRef.current
                ? getPreferredInsertionPlane(state, manualInsertionPlane)
                : null;

            const hasDragPlaneIntersection = preferredDragPlane
                ? event.ray.intersectPlane(preferredDragPlane, planeIntersectPoint)
                : event.ray.intersectPlane(raycastPlane, planeIntersectPoint);

            if (!hasDragPlaneIntersection && !event.ray.intersectPlane(raycastPlane, planeIntersectPoint)) {
                return;
            }

            if (!hasPreviousDragPoint) {
                targetPosition.copy(planeIntersectPoint);
                hasPreviousDragPoint = true;
            }

            // Reset drag state for per-frame instance grouping and anchor delta
            instancesByArticleId.clear();
            anchorMovedDelta.set(0, 0, 0);

            // When dragging-box mode is active then move the entire selection as a rigid group
            const useDraggingBoxMovement = selectedInstanceIds.length > DRAGGING_BOX_THRESHOLD;
            if (isConnectorDrag) {
                if (event.ray.intersectPlane(raycastPlane, planeIntersectPoint)) {
                    const manualConstraintNormal = manualInsertionPlane?.normal ?? workingPlane?.normal ?? null;
                    alignSelectionToCursorPoint(
                        state,
                        planeIntersectPoint,
                        snapAnchorInstanceId,
                        manualConstraintNormal
                    );
                }
            }

            dragDelta.subVectors(planeIntersectPoint, targetPosition);

            if (state.isZAxisDragActiveRef.current) {
                dragDelta.x = 0;
                dragDelta.y = 0;
            } else if (!isMixedAlignmentDrag && sharedAlignmentNormal.lengthSq() > 0) {
                projectDeltaToWorkingPlane(dragDelta, sharedAlignmentNormal);
            }

            // Lock Z axis when top/bottom view drag is active
            if (isTopBottomViewDrag) {
                dragDelta.z = 0;
            }

            const loopInstanceIds =
                (useDraggingBoxMovement || isGroupDragActive) && snapAnchorInstanceId
                    ? [snapAnchorInstanceId, ...selectedInstanceIds.filter((id) => id !== snapAnchorInstanceId)]
                    : selectedInstanceIds;

            for (const instanceId of loopInstanceIds) {
                const articleId = instanceToArticleIdMap.get(instanceId);
                const articleDragRef = articleId ? dragRefsMap.get(articleId) : undefined;
                if (!articleId || !articleDragRef) {
                    continue;
                }
                const instanceData = articleDragRef.articleRef.current?.articleInstances[instanceId];
                if (!instanceData) {
                    continue;
                }

                if (state.isZAxisDragActiveRef.current) {
                    if (dragDelta.x !== 0 || dragDelta.y !== 0 || dragDelta.z !== 0) {
                        applyMoveToInstance(instanceId, instanceData, dragDelta);
                    }
                } else if (
                    (useDraggingBoxMovement || (isGroupDragActive && groupMemberIds.has(instanceId))) &&
                    instanceId !== snapAnchorInstanceId
                ) {
                    // Rigid group: for dragging-box mode and for group dragging
                    if (anchorMovedDelta.x !== 0 || anchorMovedDelta.y !== 0 || anchorMovedDelta.z !== 0) {
                        applyMoveToInstance(instanceId, instanceData, anchorMovedDelta);
                    }
                } else if (
                    computePerInstanceWallDelta(instanceId, event.ray, isTopBottomViewDrag, dragDelta, perInstanceDelta)
                ) {
                    applyMoveToInstance(instanceId, instanceData, perInstanceDelta);
                    if (instanceId === snapAnchorInstanceId) {
                        anchorMovedDelta.copy(perInstanceDelta);
                    }
                }

                appendGroupedInstance(instancesByArticleId, articleId, instanceId);
            }

            {
                const anchorId = snapAnchorInstanceId ?? selectedInstanceIds[0];
                const { anchorInstanceData: liveAnchorData } = getAnchorDragContext(anchorId, dragRefsMap);
                if (liveAnchorData && getAnchorPosition(liveAnchorData, anchorPosition)) {
                    if (!dragAnchorPositionRef.current) {
                        dragAnchorPositionRef.current = anchorPosition.clone();
                    } else {
                        dragAnchorPositionRef.current.copy(anchorPosition);
                    }

                    raycastPlane.setFromNormalAndCoplanarPoint(raycastPlane.normal, anchorPosition);
                }
            }

            targetPosition.copy(planeIntersectPoint);

            // Translate dragging box by the anchor's wall-plane
            if (draggingBoxStatus.isComputed && !isConnectorDrag) {
                const boxDelta = anchorMovedDelta.lengthSq() > 0 ? anchorMovedDelta : dragDelta;
                if (boxDelta.x !== 0 || boxDelta.y !== 0 || boxDelta.z !== 0) {
                    translateDraggingBox(state.draggingBoxRef.current, boxDelta);
                }
            }

            const anchorId = snapAnchorInstanceId ?? selectedInstanceIds[0];
            const useDraggingBoxSnap = useDraggingBoxMovement;
            let snapPoints: Vector3[] | null = null;
            let isCornerSnap = false;

            // pre-compute anchor instance data for snap cache delta-correction
            const { anchorInstanceData: snapAnchorInstance } = getAnchorDragContext(anchorId, dragRefsMap);

            // throttle snapping computation to avoid blocking the main thread
            const now = performance.now();
            const shouldComputeSnap = !event.altKey && now - lastSnapTime >= SNAP_THROTTLE_MS;

            if (isConnectorDrag && shouldComputeSnap) {
                const adjustedSnapSource = getConnectorSnapSource(
                    planeIntersectPoint,
                    manualInsertionPlane,
                    state.workingPlane
                );

                const captureDistance = 0.2;

                let resolvedManualTarget = findNearestConnectorSnapCandidate(
                    adjustedSnapSource,
                    state.connectorSnapData.candidates,
                    captureDistance
                );

                if (
                    !resolvedManualTarget &&
                    connectorSnapCandidateIndex !== null &&
                    connectorSnapCandidateIndex >= 0 &&
                    connectorSnapCandidateIndex < state.connectorSnapData.candidates.length
                ) {
                    const latchedCandidate = state.connectorSnapData.candidates[connectorSnapCandidateIndex];

                    const distance = getDistance3D(adjustedSnapSource, latchedCandidate.position);

                    if (distance < captureDistance * 0.75) {
                        resolvedManualTarget = latchedCandidate;
                    } else {
                        connectorSnapCandidateIndex = null;
                    }
                }

                if (resolvedManualTarget) {
                    if (connectorSnapCandidateIndex === null) {
                        connectorSnapCandidateIndex = state.connectorSnapData.candidates.findIndex(
                            (c) => c === resolvedManualTarget
                        );
                    }

                    const snapTarget = new Vector3(
                        resolvedManualTarget.position.x,
                        resolvedManualTarget.position.y,
                        resolvedManualTarget.position.z
                    );

                    const manualConstraintNormal = manualInsertionPlane?.normal ?? state.workingPlane?.normal ?? null;

                    const didSnap = alignSelectionToCursorPoint(
                        state,
                        snapTarget,
                        snapAnchorInstanceId,
                        manualConstraintNormal
                    );

                    if (didSnap) {
                        targetPosition.copy(snapTarget);
                    }

                    applyRotationToSelectedInstances(selectedInstanceIds, dragRefsMap, resolvedManualTarget.rotation);

                    state.snapPointMarkerRef.current?.position.copy(snapTarget);
                } else {
                    connectorSnapCandidateIndex = null;
                }
            } else if (shouldComputeSnap) {
                lastSnapTime = now;

                // Handle corner snapping
                if (anchorId) {
                    snapPoints = state.calculateSnappingPosition(anchorId, false);
                }

                if (!snapPoints && !useDraggingBoxSnap) {
                    for (const candidateId of selectedInstanceIds) {
                        if (candidateId === anchorId) {
                            continue;
                        }
                        snapPoints = state.calculateSnappingPosition(candidateId, false);
                        if (snapPoints) {
                            break;
                        }
                    }
                }

                if (snapPoints) {
                    isCornerSnap = true;
                }

                // Handle dragging-box
                if (!snapPoints && useDraggingBoxSnap) {
                    const draggingBox = state.draggingBoxRef.current;

                    if (!draggingBox.boundingBox.isEmpty()) {
                        const { min, max } = draggingBox.boundingBox;
                        const localToWorld = draggingBox.localToWorld;

                        let closestCornerSnap: { corner: Vector3; target: Vector3; distance: number } | null = null;
                        let closestEdgeSnap: { corner: Vector3; target: Vector3; distance: number } | null = null;

                        for (let xi = 0; xi <= 1; xi++) {
                            for (let yi = 0; yi <= 1; yi++) {
                                for (let zi = 0; zi <= 1; zi++) {
                                    worldCorner
                                        .set(
                                            xi === 0 ? min.x : max.x,
                                            yi === 0 ? min.y : max.y,
                                            zi === 0 ? min.z : max.z
                                        )
                                        .applyMatrix4(localToWorld);

                                    if (workingPlane) {
                                        workingPlane.projectPoint(worldCorner, projectedCorner);
                                    } else {
                                        projectedCorner.copy(worldCorner);
                                    }

                                    const snapResult = state.findNearest(projectedCorner, {
                                        excludeRelationId: anchorId,
                                        maxDistance: 0.1,
                                        maxResultsCount: 1,
                                        neighborExpansionFactor: 2
                                    });

                                    if (snapResult) {
                                        const distance = projectedCorner.distanceTo(snapResult.position);
                                        if (!closestCornerSnap || distance < closestCornerSnap.distance) {
                                            closestCornerSnap = {
                                                corner: projectedCorner.clone(),
                                                target: snapResult.position.clone(),
                                                distance
                                            };
                                        }
                                    } else {
                                        const edgePoint = state.findNearestPointOnBuildingEdges(
                                            projectedCorner,
                                            0.1,
                                            activePoiGuids
                                        );
                                        if (edgePoint) {
                                            const distance = projectedCorner.distanceTo(edgePoint);
                                            if (!closestEdgeSnap || distance < closestEdgeSnap.distance) {
                                                closestEdgeSnap = {
                                                    corner: projectedCorner.clone(),
                                                    target: edgePoint,
                                                    distance
                                                };
                                            }
                                        }
                                    }
                                }
                            }
                        }

                        if (closestCornerSnap) {
                            snapPoints = [closestCornerSnap.target, closestCornerSnap.corner];
                            isCornerSnap = true;
                        } else if (closestEdgeSnap) {
                            snapPoints = [closestEdgeSnap.target, closestEdgeSnap.corner];
                        }
                    }
                }

                // Handle edge snapping when no corner snapping points are found
                if (!snapPoints) {
                    if (anchorId) {
                        snapPoints = state.calculateSnappingPosition(anchorId, true);
                    }
                    if (!snapPoints && !useDraggingBoxSnap) {
                        for (const candidateId of selectedInstanceIds) {
                            if (candidateId === anchorId) {
                                continue;
                            }
                            snapPoints = state.calculateSnappingPosition(candidateId, true);
                            if (snapPoints) {
                                break;
                            }
                        }
                    }
                }

                cachedSnapPoints = snapPoints;
                isCornerSnapActive = isCornerSnap;
                cachedIntersect.copy(planeIntersectPoint);
                if (snapAnchorInstance && getAnchorPosition(snapAnchorInstance, cachedSnapAnchorPosition)) {
                    if (hasDragStartReferenceOffset) {
                        cachedSnapAnchorPosition.add(dragStartReferenceOffset);
                    }
                    hasCachedSnapAnchorPosition = true;
                } else {
                    hasCachedSnapAnchorPosition = false;
                }
            } else {
                // reuse last computed snapping point, adjusted for how much the anchor moved since
                if (cachedSnapPoints) {
                    if (
                        snapAnchorInstance &&
                        hasCachedSnapAnchorPosition &&
                        getAnchorPosition(snapAnchorInstance, currentSnapAnchorPosition)
                    ) {
                        if (hasDragStartReferenceOffset) {
                            currentSnapAnchorPosition.add(dragStartReferenceOffset);
                        }
                        adjustedSnapSource
                            .copy(cachedSnapPoints[1])
                            .add(currentSnapAnchorPosition)
                            .sub(cachedSnapAnchorPosition);
                    } else {
                        adjustedSnapSource.copy(cachedSnapPoints[1]);
                    }

                    snapPoints = cachedSnapPoints;
                    isCornerSnap = isCornerSnapActive;
                }
            }

            if (snapPoints) {
                state.snapPointMarkerRef.current?.position.copy(snapPoints[0]);
                if (isConnectorDrag) {
                    const manualConstraintNormal = manualInsertionPlane?.normal ?? workingPlane?.normal ?? null;
                    if (
                        alignSelectionToCursorPoint(state, snapPoints[0], snapAnchorInstanceId, manualConstraintNormal)
                    ) {
                        targetPosition.copy(snapPoints[0]);
                    }

                    const snappedCandidate = findNearestConnectorSnapCandidate(
                        snapPoints[0],
                        state.connectorSnapData.candidates
                    );
                    if (snappedCandidate) {
                        applyRotationToSelectedInstances(selectedInstanceIds, dragRefsMap, snappedCandidate.rotation);
                    }
                } else {
                    if (!shouldComputeSnap && hasCachedSnapAnchorPosition) {
                        snapOffset.subVectors(snapPoints[0], adjustedSnapSource);
                    } else {
                        snapOffset.subVectors(snapPoints[0], snapPoints[1]);
                    }

                    const normal =
                        !isMixedAlignmentDrag && sharedAlignmentNormal.lengthSq() > 0 ? sharedAlignmentNormal : null;
                    if (normal) {
                        snapOffset.addScaledVector(normal, -snapOffset.dot(normal));
                    }

                    if (state.isZAxisDragActiveRef.current) {
                        snapOffset.x = 0;
                        snapOffset.y = 0;
                    }

                    if (isTopBottomViewDrag) {
                        snapOffset.z = 0;
                    }

                    applySnapOffsetToInstances(
                        selectedInstanceIds,
                        dragRefsMap,
                        snapOffset,
                        perInstancePlanes,
                        useDraggingBoxMovement ? true : isGroupDragActive ? groupMemberIds : false
                    );

                    // Snap translates instances outside of cursor delta, so keep drag baseline in sync.
                    targetPosition.add(snapOffset);

                    if (snapOffset.lengthSq() > 0) {
                        for (const [snapInstanceId, prevPt] of perInstancePrevIntersections) {
                            const plane = perInstancePlanes.get(snapInstanceId);
                            if (plane) {
                                snapProjDelta.copy(snapOffset);
                                projectDeltaToWorkingPlane(snapProjDelta, plane.normal);
                                prevPt.add(snapProjDelta);
                            }
                        }
                    }
                }

                // Also translate dragging box by the snap offset
                if (draggingBoxStatus.isComputed && !isConnectorDrag) {
                    translateDraggingBox(state.draggingBoxRef.current, snapOffset);
                }

                // Handle edges snapping marker
                if (!isCornerSnap && snapPoints.length >= 2) {
                    const snappedEdge = state.findNearestBuildingEdgeSegment(snapPoints[0], 0.02, activePoiGuids);
                    if (snappedEdge !== lastSnappedEdge) {
                        lastSnappedEdge = snappedEdge;
                        if (snappedEdge) {
                            get().setSnapEdgeMarker({
                                start: [snappedEdge.start.x, snappedEdge.start.y, snappedEdge.start.z],
                                end: [snappedEdge.end.x, snappedEdge.end.y, snappedEdge.end.z]
                            });
                        } else {
                            get().setSnapEdgeMarker(null);
                        }
                    }
                } else if (lastSnappedEdge !== null) {
                    lastSnappedEdge = null;
                    get().setSnapEdgeMarker(null);
                }
            } else {
                if (lastSnappedEdge !== null) {
                    lastSnappedEdge = null;
                    get().setSnapEdgeMarker(null);
                }
            }

            handleMarkerVisibility(state.snapPointMarkerRef, snapPoints !== null && isCornerSnap);

            const useDraggingBox = useDraggingBoxMovement;
            const draggingBoxRef = state.draggingBoxRef;

            if (useDraggingBox) {
                const draggingBox = draggingBoxRef.current;

                const anchorInstanceId = snapAnchorInstanceId ?? selectedInstanceIds[0];
                const { anchorDragRef, anchorInstanceData } = getAnchorDragContext(anchorInstanceId, dragRefsMap);

                if (!draggingBoxStatus.isComputed && !draggingBoxStatus.isPending) {
                    scheduleDraggingBoxComputation(
                        selectedInstanceIds,
                        anchorInstanceId,
                        dragRefsMap,
                        draggingBox,
                        state
                    );
                }

                if (draggingBoxStatus.isComputed && anchorInstanceData) {
                    if (!draggingBox.isActive && !draggingBox.boundingBox.isEmpty()) {
                        draggingBox.isActive = true;
                    }
                }

                // only update the anchor article instance
                if (anchorDragRef && anchorInstanceId) {
                    state.updateInstanceMatrices(true, anchorDragRef.articleRef, false, [anchorInstanceId]);
                }
            } else {
                draggingBoxRef.current.isActive = false;

                // update instance matrices
                for (const [articleId, instancesOfType] of instancesByArticleId) {
                    const articleDragRef = dragRefsMap.get(articleId);
                    if (!articleDragRef) {
                        continue;
                    }
                    state.updateInstanceMatrices(true, articleDragRef.articleRef, false, instancesOfType);
                }
            }
        }
    },

    handleOnDragArticleEnd: (isCancelled = false) => {
        const state = get();
        clearHoveredHighlight(state);

        const selectedInstanceIds = Object.keys(state.articleInstanceSelections);
        const dragRefsMap = state.articleDragRefsMap.current;

        const scheduleEdgesOverlayUpdate = () => {
            requestAnimationFrame(() => {
                get().updateArticleEdgesOverlay();
            });
        };

        // Fallback: update z position when dragging got interrupted during top/bottom view insertion
        const isDragRefRegistered = dragRefsMap.get(state.selectedArticleId ?? '') !== undefined;
        const isTopBottomViewInsertion =
            isTopBottomCameraView(raycastPlane.normal) &&
            (state.isDragNewArticleActiveRef.current || state.isDragNewArticleStartRef.current);

        if (isTopBottomViewInsertion && selectedInstanceIds.length === 1 && isDragRefRegistered) {
            placeInstanceOnWall(selectedInstanceIds[0], state.selectedArticleId, state, dragRefsMap);
        }

        const isZPostionUpdatePending =
            isTopBottomViewInsertion && (!isDragRefRegistered || selectedInstanceIds.length === 0);
        state.setIsZPositionUpdatePending(isZPostionUpdatePending);

        // Reset drag states
        state.setIsDragNewArticleActiveRef(false);
        state.setIsDragActiveRef(false);
        state.setIsDragNewArticleStartRef(false);
        state.setIsAutoDragActiveRef(false);
        state.resetHiddenPOIFilterTypes();
        state.setIsZAxisDragActiveRef(false);
        state.setSnapEdgeMarker(null);

        isDragEnabled = false;
        isGroupDragActive = false;
        groupMemberIds.clear();
        isDragStart = true;

        if (state.arePivotControlsActive) {
            state.toggleRecomputeTransformationBox();
        }

        handleDragSettings(true, state.mainWebGLRendererRef);
        handleMarkerVisibility(state.snapPointMarkerRef, false);

        const draggingBoxRef = state.draggingBoxRef;
        const isDraggingBoxActive = draggingBoxRef.current.isActive;
        if (isDraggingBoxActive) {
            draggingBoxRef.current.isActive = false;
            draggingBoxRef.current.boundingBox.makeEmpty();
        }

        const groupedByArticle = groupSelectedInstancesByArticle(selectedInstanceIds);

        // fallback if instanceToArticleIdMap is empty
        if (selectedInstanceIds.length > 0 && groupedByArticle.size === 0) {
            const articles = state.articles;
            for (const instanceId of selectedInstanceIds) {
                for (const articleId of Object.keys(articles)) {
                    if (instanceId in articles[articleId].articleInstances) {
                        instanceToArticleIdMap.set(instanceId, articleId);

                        const group = groupedByArticle.get(articleId);
                        if (group) {
                            group.push(instanceId);
                        } else {
                            groupedByArticle.set(articleId, [instanceId]);
                        }
                        break;
                    }
                }
            }
        }

        const isCancellation = isCancelled && !isDraggingBoxActive && selectedInstanceIds.length > 0;

        if (isCancellation) {
            const updateColliders = true;

            if (selectedInstanceIds.length > MATRIX_UPDATE_CHUNK_SIZE) {
                const groups = Array.from(groupedByArticle.entries());

                (async () => {
                    for (const [articleId, instanceIds] of groups) {
                        const dragRef = dragRefsMap.get(articleId);
                        if (!dragRef) {
                            continue;
                        }
                        for (let i = 0; i < instanceIds.length; i += MATRIX_UPDATE_CHUNK_SIZE) {
                            const chunk = instanceIds.slice(i, i + MATRIX_UPDATE_CHUNK_SIZE);
                            const isLastChunk = i + MATRIX_UPDATE_CHUNK_SIZE >= instanceIds.length;
                            state.updateInstanceMatrices(true, dragRef.articleRef, updateColliders, chunk);
                            if (!isLastChunk) {
                                await yieldToMain();
                            }
                        }
                    }
                })();
            } else {
                for (const [articleId, instanceIds] of groupedByArticle) {
                    const dragRef = dragRefsMap.get(articleId);
                    if (!dragRef) {
                        continue;
                    }

                    state.updateInstanceMatrices(true, dragRef.articleRef, updateColliders, instanceIds);
                }
            }
        }

        resetDragSettings();

        if (isCancellation) {
            scheduleEdgesOverlayUpdate();
            return;
        }

        if (selectedInstanceIds.length === 0) {
            scheduleEdgesOverlayUpdate();
            return;
        }

        const updatedArticleInstances: Array<{ articleId: string; instanceId: string; instance: ArticleInstance }> = [];

        for (const instanceId of selectedInstanceIds) {
            const articleId = instanceToArticleIdMap.get(instanceId) ?? state.selectedArticleId ?? undefined;
            if (!articleId) {
                continue;
            }

            const articleDragRef = dragRefsMap.get(articleId);
            const updatedInstances =
                articleDragRef?.articleRef.current?.articleInstances ?? state.articles[articleId]?.articleInstances;

            const instance = updatedInstances?.[instanceId];
            if (instance) {
                updatedArticleInstances.push({ articleId, instanceId, instance });
            }
        }

        if (updatedArticleInstances.length === 0) {
            scheduleEdgesOverlayUpdate();
            return;
        }

        state.updateArticleInstances(updatedArticleInstances, false);
    },

    handleOnArticleSelection: (event, articleRef) => {
        const state = get();
        clearHoveredHighlight(state);

        state.setIsArticleDirectlySelectedRef(true);

        pointerDownScreen.x = event.clientX;
        pointerDownScreen.y = event.clientY;
        isDragEnabled = false;

        if (state.isTransformationActiveRef.current) {
            return;
        }

        if (
            event.button !== 0 ||
            !articleRef.current ||
            event.shiftKey ||
            state.isDraggingActive() ||
            state.isAutoDragActiveRef.current
        ) {
            return;
        }

        const currentArticle = state.articles[articleRef.current.id];
        const currentInstances = currentArticle?.articleInstances ?? articleRef.current.articleInstances;
        const { index, articleInstanceId } = getArticleInstanceData(event.instanceId, currentInstances);

        if (!articleInstanceId || index === undefined) {
            return;
        }

        let showSelection = true;

        if (event.ctrlKey) {
            const instanceAlreadySelected = Object.keys(state.articleInstanceSelections).includes(articleInstanceId);
            showSelection = !instanceAlreadySelected;

            if (instanceAlreadySelected) {
                // Deselect the whole group (or just the single instance if not in a group)
                const groupMemberIds = state.getGroupMemberInstanceIds(articleInstanceId);
                for (const memberId of groupMemberIds) {
                    state.removeArticleInstanceSelection(memberId);
                    instanceToArticleIdMap.delete(memberId);
                }

                let anyInstanceSelected = false;
                for (const articleId of instanceToArticleIdMap.values()) {
                    if (articleId === articleRef.current!.id) {
                        anyInstanceSelected = true;
                        break;
                    }
                }
                if (!anyInstanceSelected && !state.isBatchModeActive()) {
                    state.articleDragRefsMap.current.delete(articleRef.current.id);
                }

                showSelection = false;
            } else {
                // Add the clicked instance and all its group members to the selection
                const groupMemberIds = state.getGroupMemberInstanceIds(articleInstanceId);
                for (const memberId of groupMemberIds) {
                    state.addArticleInstanceSelection(memberId, memberId === articleInstanceId ? index : undefined);
                    // Track articleId for each group member
                    const memberArticleId = getArticleId(memberId, state);
                    if (memberArticleId) {
                        instanceToArticleIdMap.set(memberId, memberArticleId);
                    }
                }

                // clone article instances for dragRef to ensure that zustand state doesn't get updated directly
                const dragRefClone = {
                    ...articleRef.current,
                    articleInstances: cloneArticleInstances(state.articles[articleRef.current.id])
                };

                state.articleDragRefsMap.current.set(articleRef.current.id, {
                    articleRef: { current: dragRefClone } as RefObject<ArticleReference | undefined>
                });
            }
        } else {
            const isAlreadySelected = Object.keys(state.articleInstanceSelections).includes(articleInstanceId);
            const groupMemberIds = state.getGroupMemberInstanceIds(articleInstanceId);

            if (!isAlreadySelected) {
                instanceToArticleIdMap.clear();
                if (!state.isBatchModeActive()) {
                    state.articleDragRefsMap.current.clear();
                }

                // Expand selection to include all group members
                if (groupMemberIds.length > 1) {
                    const groupSelection: Record<string, number | undefined> = {};
                    for (const memberId of groupMemberIds) {
                        groupSelection[memberId] = undefined;
                    }
                    // Ensure the clicked instance has the correct index
                    groupSelection[articleInstanceId] = index;
                    state.setArticleInstanceSelections(groupSelection);
                } else {
                    state.setArticleInstanceSelections({ [articleInstanceId]: index });
                }
            }

            for (const memberId of groupMemberIds) {
                const memberArticleId = getArticleId(memberId, state);
                if (memberArticleId) {
                    instanceToArticleIdMap.set(memberId, memberArticleId);
                }
            }

            // clone article instances for dragRef to ensure that zustand state doesn't get updated directly
            const dragRefClone = {
                ...articleRef.current,
                articleInstances: cloneArticleInstances(state.articles[articleRef.current.id])
            };

            state.articleDragRefsMap.current.set(articleRef.current.id, {
                articleRef: { current: dragRefClone } as RefObject<ArticleReference | undefined>
            });
        }

        state.updateInstanceMatrices(showSelection, articleRef, false, [articleInstanceId]);

        if (showSelection) {
            snapAnchorInstanceId = articleInstanceId;

            if (event.point) {
                dragStartPoint.copy(event.point);
                hasDragStartPoint = true;
            } else if (event.ray?.intersectPlane(raycastPlane, dragStartPoint)) {
                hasDragStartPoint = true;
            }

            // pre-compute the dragging box for large selections
            const currentSelectedIds = Object.keys(get().articleInstanceSelections);
            if (currentSelectedIds.length > DRAGGING_BOX_THRESHOLD) {
                updateInstanceToArticleIdMap(currentSelectedIds, get());
                draggingBoxStatus.isPending = false;
                draggingBoxStatus.isComputed = false;

                scheduleDraggingBoxComputation(
                    currentSelectedIds,
                    articleInstanceId,
                    state.articleDragRefsMap.current,
                    state.draggingBoxRef.current,
                    state
                );
            } else {
                draggingBoxStatus.isComputed = false;
            }
        }
    },

    handleOnArticlePointerOver: (event, articleRef) => {
        if (event.shiftKey || get().isAutoDragActiveRef.current || get().isDraggingActive() || !articleRef.current) {
            return;
        }

        const state = get();
        const { index, articleInstanceId } = getArticleInstanceData(
            event.instanceId,
            articleRef.current.articleInstances
        );
        if (index === undefined || !articleInstanceId) {
            return;
        }

        // Highlight all group members if it belongs to a group
        const groupMemberIds = state.getGroupMemberInstanceIds(articleInstanceId);
        const idsToHighlight = groupMemberIds && groupMemberIds.length > 0 ? groupMemberIds : [articleInstanceId];

        // Collect refs by article for all group members
        const refsByArticle = new Map<string, HoverArticleGroupEntry>();
        const nextHoveredInstanceIds = new Set<string>();
        for (const memberId of idsToHighlight) {
            const memberArticleId = getArticleId(memberId, state);
            if (!memberArticleId) {
                continue;
            }
            const ref = resolveHoverArticleRef(state, memberArticleId, memberId, articleRef);
            if (!ref) {
                continue;
            }
            if (!refsByArticle.has(memberArticleId)) {
                refsByArticle.set(memberArticleId, { articleId: memberArticleId, ref, instanceIds: [memberId] });
            } else {
                refsByArticle.get(memberArticleId)!.instanceIds.push(memberId);
            }
            nextHoveredInstanceIds.add(memberId);
        }

        if (nextHoveredInstanceIds.size === 0) {
            clearHoveredHighlight(state);
            return;
        }

        if (
            nextHoveredInstanceIds.size === hoveredGroupInstanceIds.size &&
            Array.from(nextHoveredInstanceIds).every((id) => hoveredGroupInstanceIds.has(id))
        ) {
            return;
        }

        clearHoveredHighlight(state);

        // Set color highlight
        for (const { ref, instanceIds } of refsByArticle.values()) {
            state.updateInstanceMatrices(true, ref, false, instanceIds, undefined, instanceIds);
        }

        hoveredGroupByArticle.clear();
        for (const [key, value] of refsByArticle.entries()) {
            hoveredGroupByArticle.set(key, value);
        }

        hoveredGroupInstanceIds.clear();
        for (const id of nextHoveredInstanceIds) {
            hoveredGroupInstanceIds.add(id);
        }

        state.setHoveredArticleInstanceIds(Array.from(nextHoveredInstanceIds));

        const mainWebGLRendererRef = state.mainWebGLRendererRef;
        if (mainWebGLRendererRef.current && mainWebGLRendererRef.current.domElement.style.cursor !== 'move') {
            mainWebGLRendererRef.current.domElement.style.cursor = 'pointer';
        }
    },

    handleOnArticlePointerOut: (event, articleRef) => {
        if (event.shiftKey || get().isDraggingActive() || !articleRef.current) {
            get().setHoveredArticleInstanceIds([]);
            return;
        }

        const state = get();
        const { index, articleInstanceId } = getArticleInstanceData(
            event.instanceId,
            articleRef.current.articleInstances
        );

        if (index === undefined || !articleInstanceId) {
            return;
        }

        if (hoveredGroupInstanceIds.has(articleInstanceId)) {
            clearHoveredHighlight(state);
        }

        const mainWebGLRendererRef = state.mainWebGLRendererRef;
        if (mainWebGLRendererRef.current) {
            mainWebGLRendererRef.current.domElement.style.cursor = 'auto';
        }
    },

    clearHoveredArticleHighlight: () => {
        clearHoveredHighlight(get());
    },

    handleOnArticleContextMenu: () => {
        get().setIsContextMenuVisible(true);
    },

    handleOnUngroupSelection: () => {
        const state = get();
        const selectedIds = Object.keys(state.articleInstanceSelections);
        const groupIds = new Set<string>();

        for (const id of selectedIds) {
            const gid = state.instanceToGroupMap[id];
            if (gid) {
                groupIds.add(gid);
            }
        }
        for (const gid of groupIds) {
            state.removeArticleGroup(gid);
        }
    }
});
